<template>
  <el-card shadow="never" class="reveal">
    <template #header>
      <div class="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span class="font-display font-bold text-profissa-text-main">Alertes antifraude</span>
          <div class="text-xs text-profissa-text-muted font-semibold mt-0.5">
            {{ filtered.length }} alerte{{ filtered.length !== 1 ? 's' : '' }} détectée{{ filtered.length !== 1 ? 's' : '' }}
          </div>
        </div>
        <div class="flex gap-3 items-center flex-wrap">
          <el-input
            v-model="localSearch"
            placeholder="Artisan, téléphone…"
            clearable
            class="w-48"
            @input="onSearchInput"
            @clear="onSearchInput"
          >
            <template #prefix><el-icon><Search /></el-icon></template>
          </el-input>
          <el-select
            :model-value="statusFilter"
            placeholder="Tous les statuts"
            clearable
            class="w-40"
            @change="$emit('update:statusFilter', $event || '')"
          >
            <el-option value="" label="Tous" />
            <el-option value="open" label="Ouverte" />
            <el-option value="investigating" label="En enquête" />
            <el-option value="confirmed" label="Confirmée" />
            <el-option value="dismissed" label="Classée" />
          </el-select>
          <el-select
            :model-value="typeFilter"
            placeholder="Tous les types"
            clearable
            class="w-48"
            @change="$emit('update:typeFilter', $event || '')"
          >
            <el-option value="" label="Tous" />
            <el-option value="photo_anomaly"     label="Anomalie photo" />
            <el-option value="price_anomaly"     label="Anomalie prix" />
            <el-option value="fake_cancellation" label="Fausse annulation" />
          </el-select>
          <el-button :loading="isLoading" circle @click="$emit('refresh')" :icon="Refresh" />
        </div>
      </div>
    </template>

    <el-empty v-if="!isLoading && !filtered.length" description="Aucune alerte" />

    <div v-if="selectedRows.length && canResolve" class="bulk-bar">
      <span class="bulk-count">{{ selectedRows.length }} sélectionné(s)</span>
      <el-button type="info" size="small" @click="bulkAction('dismissed')">Classer (Faux Positif)</el-button>
      <el-button type="danger" size="small" @click="bulkAction('confirmed')">Confirmer Fraude</el-button>
    </div>

    <el-table
      v-loading="isLoading"
      :data="paginated"
      class="custom-table w-full"
      stripe
      :row-class-name="() => 'alert-row'"
      @selection-change="onSelectionChange"
      @row-click="onRowClick"
    >
      <el-table-column type="selection" width="45" @click.stop />

      <el-table-column label="Réf." width="70">
        <template #default="{ $index }">
          <span class="font-display font-bold text-profissa-text-muted">
            #{{ String((currentPage - 1) * pageSize + $index + 1).padStart(3, '0') }}
          </span>
        </template>
      </el-table-column>

      <el-table-column label="Artisan" min-width="150">
        <template #default="{ row }">
          <div class="font-bold text-[0.9rem]">{{ row.artisan_name || '—' }}</div>
          <div class="text-xs text-profissa-text-muted font-mono mt-0.5">{{ row.artisan_phone || '' }}</div>
        </template>
      </el-table-column>

      <el-table-column label="Type d'Alerte" width="175" sortable :sort-method="(a, b) => (a.type || '').localeCompare(b.type || '')">
        <template #default="{ row }">
          <el-tag :type="alertTypeTag(row.type)" class="uppercase font-bold tracking-wider">
            {{ alertTypeBadge(row.type).label }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="Statut" width="130" sortable :sort-method="(a, b) => (a.status || '').localeCompare(b.status || '')">
        <template #default="{ row }">
          <el-tag :type="alertStatusTag(row.status)" class="uppercase font-bold tracking-wider">
            {{ alertStatusBadge(row.status).label }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="Risque IA" width="140">
        <template #default="{ row }">
          <div v-if="parseDetails(row)" class="flex flex-col gap-1.5">
            <div class="flex items-center gap-2">
              <span
                class="text-sm font-black tabular-nums"
                :style="{ color: confColor(parseDetails(row).confidence ?? 0) }"
              >
                {{ Math.round((parseDetails(row).confidence ?? 0) * 100) }}%
              </span>
              <div
                class="h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden"
              >
                <div
                  class="h-full rounded-full transition-all"
                  :style="{
                    width: Math.round((parseDetails(row).confidence ?? 0) * 100) + '%',
                    background: confColor(parseDetails(row).confidence ?? 0)
                  }"
                />
              </div>
            </div>
            <div v-if="parseDetails(row).flags?.length" class="flex flex-wrap gap-1">
              <el-tag
                v-for="flag in parseDetails(row).flags.slice(0, 2)"
                :key="flag"
                type="warning"
                size="small"
                class="!text-[0.55rem] !px-1.5 !py-0 !h-4 !leading-4"
              >{{ flag }}</el-tag>
              <span v-if="parseDetails(row).flags.length > 2" class="text-[0.65rem] text-profissa-text-muted self-center">
                +{{ parseDetails(row).flags.length - 2 }}
              </span>
            </div>
          </div>
          <span v-else class="text-profissa-text-dim">—</span>
        </template>
      </el-table-column>

      <el-table-column label="Détectée le" width="135" sortable :sort-method="(a, b) => new Date(a.created_at) - new Date(b.created_at)">
        <template #default="{ row }">
          <span class="text-xs text-profissa-text-muted">{{ fmtDate(row.created_at) }}</span>
        </template>
      </el-table-column>

      <el-table-column label="" width="60" fixed="right" align="center">
        <template #default="{ row }">
          <div @click.stop>
            <ActionDropdown
              :actions="rowActions(row)"
              @action="(key) => handleAction(key, row)"
            />
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="filtered.length > pageSize" class="flex justify-end mt-6">
      <el-pagination
        v-model:current-page="currentPage"
        :page-size="pageSize"
        :total="filtered.length"
        layout="prev, pager, next, total"
        background
      />
    </div>
  </el-card>
</template>

<script setup>
import { ref, watch, computed, defineProps, defineEmits } from 'vue'
import { Search, Refresh, View, Check, Warning, Lock } from '@element-plus/icons-vue'
import { alertTypeBadge, alertStatusBadge, fmtDate } from '../../utils/labels'
import ActionDropdown from '../shared/ActionDropdown.vue'

const props = defineProps({
  alerts:          { type: Array,   default: () => [] },
  statusFilter:    { type: String,  default: '' },
  typeFilter:      { type: String,  default: '' },
  isLoading:       { type: Boolean, default: false },
  mutatingAlertId: { type: String,  default: '' },
  searchQuery:     { type: String,  default: '' },
  canResolve:      { type: Boolean, default: false },
  canSanction:     { type: Boolean, default: false },
})

const emit = defineEmits([
  'refresh', 'events', 'detail', 'resolve', 'bulk-resolve',
  'update:statusFilter', 'update:typeFilter', 'search', 'sanction-artisan',
])

const selectedRows = ref([])
const localSearch  = ref(props.searchQuery)
const currentPage  = ref(1)
const pageSize     = 15
let debounceTimer  = null

watch(() => props.searchQuery, (v) => { localSearch.value = v })

const onSelectionChange = (rows) => { selectedRows.value = rows }

const bulkAction = (status) => {
  if (!props.canResolve) return
  const actionable = selectedRows.value.filter(r => r.status === 'open' || r.status === 'investigating')
  if (!actionable.length) return
  for (const row of actionable) emit('resolve', { id: row.id, status })
}

const onSearchInput = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => emit('search', localSearch.value), 350)
}

const onRowClick = (row) => {
  emit('detail', row)
}

const parseDetails = (alert) => {
  if (!alert.details) return null
  try {
    const d = typeof alert.details === 'string' ? JSON.parse(alert.details) : alert.details
    return (d && typeof d === 'object') ? d : null
  } catch { return null }
}

const confColor = (v) => v >= 0.75 ? 'var(--el-color-danger)' : v >= 0.5 ? 'var(--el-color-warning)' : 'var(--el-color-success)'

const alertTypeTag = (t) => ({
  photo_anomaly: 'danger', price_anomaly: 'danger',
  fake_cancellation: 'warning',
}[t] || 'info')

const alertStatusTag = (s) => ({
  open: 'danger', investigating: 'warning', confirmed: 'danger', dismissed: 'info'
}[s] || 'info')

const isActionable = (status) => status === 'open' || status === 'investigating'

const filtered = computed(() => {
  let rows = props.alerts
  if (localSearch.value) {
    const q = localSearch.value.toLowerCase()
    rows = rows.filter(r =>
      (r.artisan_name || '').toLowerCase().includes(q) ||
      (r.artisan_phone || '').includes(localSearch.value)
    )
  }
  return rows
})

const paginated = computed(() => {
  const start = (currentPage.value - 1) * pageSize
  return filtered.value.slice(start, start + pageSize)
})

watch(filtered, () => { currentPage.value = 1 })

const rowActions = (row) => {
  const actions = [
    { key: 'events', label: 'Voir le détail', icon: View, disabled: props.mutatingAlertId === row.id },
  ]
  if (props.canResolve && isActionable(row.status)) {
    actions.push({ key: 'dismiss', label: 'Classer (Faux Positif)',  icon: Check,   disabled: props.mutatingAlertId === row.id, divided: true })
    actions.push({ key: 'confirm', label: 'Confirmer la Fraude',     icon: Warning, danger: true, disabled: props.mutatingAlertId === row.id })
  }
  if (props.canSanction && row.artisan_id) {
    actions.push({ key: 'sanction', label: "Sanctionner l'artisan", icon: Lock, divided: true, disabled: props.mutatingAlertId === row.id })
  }
  return actions
}

const handleAction = (key, row) => {
  if (key === 'events') {
    emit('detail', row)
  } else if (key === 'dismiss') {
    if (!props.canResolve) return
    emit('resolve', { id: row.id, status: 'dismissed' })
  } else if (key === 'confirm') {
    if (!props.canResolve) return
    emit('resolve', { id: row.id, status: 'confirmed' })
  } else if (key === 'sanction') {
    if (!props.canSanction) return
    emit('sanction-artisan', { artisanId: row.artisan_id, artisanName: row.artisan_name })
  }
}
</script>

<style scoped>
.bulk-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border-radius: 12px;
  background: rgba(255, 107, 0, 0.06);
  border: 1px solid rgba(255, 107, 0, 0.15);
}
.bulk-count { font-weight: 800; font-size: 0.85rem; color: var(--accent); margin-right: auto; }

:deep(.alert-row) { cursor: pointer; transition: background 0.15s; }
:deep(.alert-row:hover td) { background: var(--surface-hover) !important; }
</style>
